import { daysToSeconds } from '~core/plugins/helpers/time-conversion'
import {
  YandexECommerceEventType,
  type YandexECommerceProduct,
} from '~/models/ecommerce-yandex'
import { Currency, type OrderResponse } from '~/models/orders'
import type { EventLite } from '~/models/events'
import { useECommerceHelper } from '~/composables/use-ecommerce-helper'
import type { GoogleECommerceItem } from '~/models/ecommerce-google'

export const useECommercePayment = () => {
  const {
    $yandexMetrika: {
      sendYandexECommerceEvent,
      getYandexECommerceProductFromTicket,
      getYandexECommerceGoalId,
    },
    $linkedin: { lintrk },
    $facebook: { fbq },
    $gtm: {
      gTagPush,
      getGoogleECommerceItemFromTicket,
      sendPurchaseGoogleECommerceEvent,
    },
    $hhCareer,
  } = useNuxtApp()

  const { prepareECommerceOrderId, prepareECommercePrice } =
    useECommerceHelper()

  async function sendECommercePurchaseEvent(
    order: OrderResponse,
    event?: EventLite
  ): Promise<void> {
    const { cookie, load } = useAppCookie<number[] | null>(
      AppCookieKeys.ECommerceSentOrders,
      {
        maxAge: daysToSeconds(365),
        domain: useDomain(),
      }
    )

    await load()

    cookie.value ??= []

    if (cookie.value.includes(order.id)) {
      return
    }

    const quantityMap = {} as { [key: number]: number }

    order.items.forEach((orderItem) => {
      if (!('ticket' in orderItem.details)) {
        return
      }
      const ticketId = orderItem.details?.ticket?.id

      if (ticketId) {
        quantityMap[ticketId] ??= 0
        quantityMap[ticketId]++
      }
    })

    const yandexProducts: YandexECommerceProduct[] = []
    const googleItems: GoogleECommerceItem[] = []

    order.items.forEach((orderItem) => {
      if (!('ticket' in orderItem.details)) {
        return
      }
      const ticketId = orderItem.details?.ticket?.id
      if (!ticketId) {
        return
      }

      const quantity = quantityMap[ticketId]

      if (!quantity) {
        return
      }

      quantityMap[ticketId] = 0

      const fullPrice = orderItem.details.ticket.prices?.[order.currency] || 0

      const yandexProduct = getYandexECommerceProductFromTicket(
        orderItem.details.ticket,
        order.currency,
        event,
        quantity,
        order.promo_code_code,
        Math.max(fullPrice - orderItem.amount, 0)
      )

      const googleItem = getGoogleECommerceItemFromTicket(
        orderItem.details.ticket,
        order.currency,
        event,
        quantity,
        order.promo_code_code,
        Math.max(fullPrice - orderItem.amount, 0)
      )

      yandexProducts.push(yandexProduct)
      googleItems.push(googleItem)
    })

    if (yandexProducts.length) {
      sendYandexECommerceEvent({
        currencyCode: order.currency,
        [YandexECommerceEventType.Purchase]: {
          actionField: {
            id: prepareECommerceOrderId(order.id),
            goal_id: getYandexECommerceGoalId(),
            coupon: order.promo_code_code,
            revenue: prepareECommercePrice(order.amount),
          },
          products: yandexProducts,
        },
      })
    }

    if (googleItems.length) {
      sendPurchaseGoogleECommerceEvent(
        {
          transaction_id: prepareECommerceOrderId(order.id),
          currency: order.currency,
          coupon: order.promo_code_code,
          value: prepareECommercePrice(order.amount),
        },
        googleItems
      )
    }

    cookie.value = [...cookie.value, order.id]
  }

  async function sendHHCareerPaidEvent(
    order: OrderResponse,
    event?: EventLite
  ): Promise<void> {
    try {
      if (order.currency === Currency.RUB && event) {
        await $hhCareer.sendPaidEvent(
          event.id,
          event.name,
          order.amount / 100,
          order.promo_code_code ?? null
        )
      }
    } catch (error: any) {
      useLogError(error)
    }
  }

  async function processOrderSuccess(order: OrderResponse, event?: EventLite) {
    lintrk('track', { conversion_id: 17206322 })
    fbq('track', 'Purchase', {
      value: order.amount || 0,
      currency: order.currency || 'USD',
    })
    gTagPush({
      event: 'Purchase',
      purchase_amount: order.amount || 0,
      purchase_currency: order.currency || 'USD',
    })

    sendHHCareerPaidEvent(order, event)

    await sendECommercePurchaseEvent(order, event)
  }

  return {
    processOrderSuccess,
  }
}
